import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Card, Tag, Button, Drawer, Spin, Empty, Badge, message,
  Select, Input, Row, Col, Statistic, Tabs, Form, Modal, Avatar, Tooltip
} from "antd";
import {
  ExclamationCircleOutlined, StarOutlined, FileTextOutlined,
  CheckCircleOutlined, QuestionCircleOutlined, EditOutlined, PlusOutlined,
  AlertOutlined, PercentageOutlined, CloseCircleOutlined, EyeOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONST } from "../../../const";
import styled from "styled-components";
import TalentFeedbackModal from "../../../components/TalentFeedbackModal/TalentFeedbackModal";

const { TextArea } = Input;
const { Option } = Select;

const Wrapper = styled.div`
  .page-header { margin-bottom: 24px; }
  .page-header h2 { color: #014c75; font-size: 22px; font-weight: 700; margin: 0; }
  .page-header p { color: #888; margin: 4px 0 0; }
  .stats-row { margin-bottom: 24px; }
  .stat-card { border-radius: 8px; text-align: center; }
  .feedback-card { border-radius: 8px; margin-bottom: 10px; border: 1px solid #e6f0f8; }
  .response-item { padding: 8px 12px; background: #f8fbfe; border-radius: 6px; margin-bottom: 6px; }
`;

const SUBTYPE_COLOR = { concern: "error", warning: "orange", behavioral_recognition: "success", general: "processing" };
const SUBTYPE_LABEL = { concern: "Concern", warning: "Warning", behavioral_recognition: "Behavioral / Recognition", general: "General" };
const BUCKET_LABEL = { need_more_time: "Need More Time", not_meeting: "Not Meeting", meets_expectations: "Meets Expectations", impressive: "Impressive", excellent: "Excellent" };
const BUCKET_COLOR = { need_more_time: "default", not_meeting: "error", meets_expectations: "processing", impressive: "success", excellent: "gold" };
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const TYPE_LABEL = { instant: "Instant", monthly: "Monthly Review", contract_end: "Contract End" };
const TYPE_COLOR = { instant: "orange", monthly: "blue", contract_end: "purple" };

const AdminTalentPerformance = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [responding, setResponding] = useState(false);
  const [modifyVisible, setModifyVisible] = useState(false);
  const [responseForm] = Form.useForm();
  const [modifyForm] = Form.useForm();
  const [addFeedbackVisible, setAddFeedbackVisible] = useState(false);
  const [selectedFeedbackForAction, setSelectedFeedbackForAction] = useState(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_CONST.PERFORMANCE_FEEDBACK_STATS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data.stats);
    } catch (e) {
      console.error("Stats fetch failed", e);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = { page, pageSize, ...filters };
      const res = await axios.get(API_CONST.PERFORMANCE_FEEDBACK, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedback(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      message.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const openDrawer = async (item) => {
    setSelectedItem(item);
    setSelectedFeedbackForAction(item);
    setDrawerOpen(true);
  };

  const handleRespond = async (responseType) => {
    try {
      const values = await responseForm.validateFields();
      setResponding(true);
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.PERFORMANCE_FEEDBACK}/${selectedFeedbackForAction.id}/response`,
        { responseType, content: values.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(responseType === "acknowledgment" ? "Acknowledged" : "Query raised");
      responseForm.resetFields();
      await fetchFeedback();
      const refreshed = feedback.find(f => f.id === selectedFeedbackForAction.id);
      if (refreshed) setSelectedItem(refreshed);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "Failed to respond");
    } finally {
      setResponding(false);
    }
  };

  const handleModify = async () => {
    try {
      const values = await modifyForm.validateFields();
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_CONST.PERFORMANCE_FEEDBACK}/${selectedFeedbackForAction.id}`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Feedback updated");
      setModifyVisible(false);
      modifyForm.resetFields();
      fetchFeedback();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to update feedback");
    }
  };

  const handleResolve = async () => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_CONST.PERFORMANCE_FEEDBACK}/${selectedFeedbackForAction.id}`,
        { status: "closed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Feedback marked as resolved");
      setDrawerOpen(false);
      setSelectedItem(null);
      fetchFeedback();
      fetchStats();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to resolve");
    }
  };

  const columns = [
    {
      title: "Talent",
      dataIndex: "talent",
      render: (t, r) => (
        <div>
          <div
            style={{ fontWeight: 600, color: "#014c75", cursor: "pointer" }}
            onClick={() => navigate(`/admin/talents/performance/${r.talentId}`)}
          >
            {t?.name || r.talentId}
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>{t?.role || ""}</div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "feedbackType",
      render: (v, r) => (
        <div>
          <Tag color={TYPE_COLOR[v]}>{TYPE_LABEL[v] || v}</Tag>
          {v === "instant" && r.instantSubType && (
            <Tag color={SUBTYPE_COLOR[r.instantSubType]} style={{ marginTop: 2 }}>
              {SUBTYPE_LABEL[r.instantSubType]}
            </Tag>
          )}
          {v === "monthly" && r.month && r.year && (
            <div style={{ fontSize: 11, color: "#888" }}>{MONTH_NAMES[r.month - 1]} {r.year}</div>
          )}
        </div>
      ),
    },
    {
      title: "Partner Org",
      dataIndex: "partnerOrganization",
      render: v => <span style={{ fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Customer Org",
      dataIndex: "customerOrganization",
      render: v => <span style={{ fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      width: 130,
      render: v => v ? (
        <Tooltip title={v}>
          <Tag icon={<FileTextOutlined />} color="blue" style={{ borderRadius: 6, fontSize: 11, cursor: "default" }}>
            {v}
          </Tag>
        </Tooltip>
      ) : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>,
    },
    {
      title: "Contract ID",
      dataIndex: "hiringRecordId",
      width: 140,
      render: v => v ? (
        <Tooltip title={v}>
          <Tag color="geekblue" style={{ borderRadius: 6, fontSize: 11, cursor: "default" }}>
            {v}
          </Tag>
        </Tooltip>
      ) : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>,
    },
    {
      title: "Score",
      dataIndex: "ratingBucket",
      render: (v, r) => v ? (
        <Tooltip title={BUCKET_LABEL[v]}>
          <Tag color={BUCKET_COLOR[v]}>{r.overallScore}%</Tag>
        </Tooltip>
      ) : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: v => (
        <Tag color={v === "open" ? "red" : v === "acknowledged_by_partner" ? "green" : "default"}>
          {v === "open" ? "Open" : v === "acknowledged_by_partner" ? "Acknowledged" : "Closed"}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: v => new Date(v).toLocaleDateString("en-IN"),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <Button size="small" type="primary" style={{ background: "#014c75" }} onClick={() => openDrawer(r)}>
          Review
        </Button>
      ),
    },
  ];

  return (
    <Wrapper>
      <div className="page-header">
        <h2>Talent Performance</h2>
        <p>Monitor and manage all talent performance feedback across customers and partners</p>
      </div>

      <Row gutter={16} className="stats-row">
        {[
          { title: "Total Feedback", value: stats?.total, icon: <FileTextOutlined />, color: "#014c75" },
          { title: "Open Concerns", value: stats?.openConcerns, icon: <AlertOutlined />, color: "#ff4d4f" },
          { title: "Open Queries", value: stats?.openQueries, icon: <QuestionCircleOutlined />, color: "#fa8c16" },
          { title: "Avg Monthly Score", value: stats?.avgMonthlyScore !== null ? `${stats?.avgMonthlyScore}%` : "N/A", icon: <PercentageOutlined />, color: "#01d9a9", suffix: "" },
        ].map((s, i) => (
          <Col span={6} key={i}>
            <Card className="stat-card" loading={statsLoading}>
              <Statistic
                title={s.title}
                value={s.value ?? "—"}
                prefix={<span style={{ color: s.color }}>{s.icon}</span>}
                valueStyle={{ color: s.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 8, marginBottom: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Select
              placeholder="Type"
              allowClear
              style={{ width: "100%" }}
              onChange={v => setFilters(f => ({ ...f, feedbackType: v }))}
            >
              <Option value="instant">Instant</Option>
              <Option value="monthly">Monthly</Option>
              <Option value="contract_end">Contract End</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: "100%" }}
              onChange={v => setFilters(f => ({ ...f, status: v }))}
            >
              <Option value="open">Open</Option>
              <Option value="acknowledged_by_partner">Acknowledged</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </Col>
          <Col span={5}>
            <Input
              placeholder="Partner org..."
              allowClear
              onChange={e => setFilters(f => ({ ...f, partnerOrganization: e.target.value || undefined }))}
            />
          </Col>
          <Col span={5}>
            <Input
              placeholder="Customer org..."
              allowClear
              onChange={e => setFilters(f => ({ ...f, customerOrganization: e.target.value || undefined }))}
            />
          </Col>
          <Col span={4}>
            <Input
              placeholder="Talent ID..."
              allowClear
              onChange={e => setFilters(f => ({ ...f, talentId: e.target.value || undefined }))}
            />
          </Col>
          <Col span={2}>
            <Button onClick={() => { setFilters({}); setPage(1); }}>Reset</Button>
          </Col>
        </Row>

        <Table
          dataSource={feedback}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (t) => `${t} feedback items`,
          }}
        />
      </Card>

      <Drawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedItem(null); }}
        title="Feedback Detail"
        width={600}
        destroyOnClose
      >
        {selectedItem && (
          <div>
            <div style={{ marginBottom: 16, padding: "12px 16px", background: "#f0f5ff", borderRadius: 8 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <Tag color={TYPE_COLOR[selectedItem.feedbackType]}>{TYPE_LABEL[selectedItem.feedbackType]}</Tag>
                {selectedItem.instantSubType && (
                  <Tag color={SUBTYPE_COLOR[selectedItem.instantSubType]}>{SUBTYPE_LABEL[selectedItem.instantSubType]}</Tag>
                )}
                {selectedItem.severity && <Tag color={selectedItem.severity === "high" ? "red" : "orange"}>{selectedItem.severity.toUpperCase()}</Tag>}
                <Tag color={selectedItem.status === "open" ? "red" : "green"}>
                  {selectedItem.status === "open" ? "Open" : selectedItem.status === "acknowledged_by_partner" ? "Acknowledged" : "Closed"}
                </Tag>
              </div>
              {selectedItem.title && <div style={{ fontWeight: 600, marginBottom: 4 }}>{selectedItem.title}</div>}
              <div style={{ fontSize: 13, color: "#555" }}>{selectedItem.description || selectedItem.comments || ""}</div>
              {selectedItem.ratingBucket && (
                <div style={{ marginTop: 8 }}>
                  <Tag color={BUCKET_COLOR[selectedItem.ratingBucket]}>{BUCKET_LABEL[selectedItem.ratingBucket]}</Tag>
                  {selectedItem.overallScore !== null && <strong style={{ color: "#014c75" }}> {selectedItem.overallScore}%</strong>}
                </div>
              )}
              {selectedItem.recommendation && (
                <div style={{ marginTop: 4, fontSize: 13 }}>
                  Recommendation: <Tag>{selectedItem.recommendation.replace("_", " ")}</Tag>
                  {selectedItem.wouldRehire && <> · Would Rehire: <Tag>{selectedItem.wouldRehire}</Tag></>}
                </div>
              )}
              {selectedItem.coreStrengths && <div style={{ marginTop: 6, fontSize: 12 }}><strong>Strengths:</strong> {selectedItem.coreStrengths}</div>}
              {selectedItem.areasOfImprovement && <div style={{ fontSize: 12 }}><strong>Improvements:</strong> {selectedItem.areasOfImprovement}</div>}
              {selectedItem.keyAchievements && <div style={{ fontSize: 12 }}><strong>Achievements:</strong> {selectedItem.keyAchievements}</div>}
              <div style={{ marginTop: 8, fontSize: 12, color: "#aaa" }}>
                Raised by: {selectedItem.raisedBy} · {new Date(selectedItem.createdAt).toLocaleString("en-IN")}
                {selectedItem.editedAt && <> · Edited {new Date(selectedItem.editedAt).toLocaleDateString("en-IN")}</>}
              </div>
            </div>

            {selectedItem.responses && selectedItem.responses.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: "#014c75" }}>Responses ({selectedItem.responses.length})</div>
                {selectedItem.responses.map(r => {
                  const isAdmin = r.respondedByRole === "admin";
                  const isCustomer = r.respondedByRole === "customer";
                  return (
                  <div key={r.id} style={{
                    padding: "8px 12px",
                    background: isAdmin ? "#f6f8ff" : isCustomer ? "#f0fff4" : "#f8fbfe",
                    border: "1px solid #e6f0f8",
                    borderLeft: `3px solid ${isAdmin ? "#4096ff" : isCustomer ? "#52c41a" : "#91caff"}`,
                    borderRadius: 6,
                    marginBottom: 6,
                  }}>
                    <div style={{ fontSize: 12, color: "#888" }}>
                      <strong style={{ color: isAdmin ? "#1677ff" : isCustomer ? "#389e0d" : "#555" }}>
                        {isAdmin ? "Admin" : isCustomer ? "Customer" : "Partner"}
                      </strong>
                      {" · "}<Tag style={{ fontSize: 10, borderRadius: 20, padding: "0 6px" }}>
                        {r.responseType?.replace(/_/g, " ")}
                      </Tag>
                      {" · "}{new Date(r.createdAt).toLocaleDateString("en-IN")}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>{r.content}</div>
                  </div>
                );
                })}
              </div>
            )}

            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 12, color: "#014c75" }}>Admin Actions</div>
              <Form form={responseForm} layout="vertical">
                <Form.Item name="content" rules={[{ required: true, message: "Enter content" }]}>
                  <TextArea rows={3} placeholder="Add a query, note, or intervention..." maxLength={2000} />
                </Form.Item>
              </Form>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button icon={<CheckCircleOutlined />} loading={responding} onClick={() => handleRespond("acknowledgment")}>
                  Acknowledge
                </Button>
                <Button icon={<QuestionCircleOutlined />} loading={responding} onClick={() => handleRespond("query")}>
                  Raise Query
                </Button>
                <Button icon={<FileTextOutlined />} loading={responding} onClick={() => handleRespond("admin_note")}>
                  Add Note
                </Button>
                <Button icon={<EditOutlined />} onClick={() => {
                  modifyForm.setFieldsValue({ status: selectedItem.status, title: selectedItem.title, description: selectedItem.description });
                  setModifyVisible(true);
                }}>
                  Modify
                </Button>
                {selectedItem?.status !== "closed" && (
                  <Button
                    icon={<CloseCircleOutlined />}
                    style={{ color: "#52c41a", borderColor: "#b7eb8f" }}
                    onClick={handleResolve}
                  >
                    Mark Resolved
                  </Button>
                )}
                <Button
                  icon={<EyeOutlined />}
                  type="link"
                  style={{ paddingLeft: 0 }}
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate(`/admin/talents/performance/${selectedItem?.talentId}`);
                  }}
                >
                  Full Profile
                </Button>
                <Button
                  icon={<PlusOutlined />}
                  type="dashed"
                  onClick={() => setAddFeedbackVisible(true)}
                >
                  New Entry
                </Button>
              </div>

              {selectedItem.editHistory && selectedItem.editHistory.length > 0 && (
                <div style={{ marginTop: 16, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>Edit Audit Trail ({selectedItem.editHistory.length} edit{selectedItem.editHistory.length > 1 ? "s" : ""})</div>
                  {selectedItem.editHistory.map((h, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#bbb", marginBottom: 3 }}>
                      {new Date(h.editedAt).toLocaleString("en-IN")} by {h.editedBy}
                      {" — changed: "}{Object.keys(h.previousValues).join(", ")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        open={modifyVisible}
        onCancel={() => { setModifyVisible(false); modifyForm.resetFields(); }}
        title="Modify Feedback"
        onOk={handleModify}
        okText="Save Changes"
      >
        <Form form={modifyForm} layout="vertical">
          <Form.Item name="title" label="Title">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="description" label="Description / Comments">
            <TextArea rows={4} maxLength={2000} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="open">Open</Option>
              <Option value="acknowledged_by_partner">Acknowledged by Partner</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <TalentFeedbackModal
        open={addFeedbackVisible}
        onClose={() => setAddFeedbackVisible(false)}
        onSuccess={() => { fetchFeedback(); fetchStats(); }}
        hiringRecordId={selectedFeedbackForAction?.hiringRecordId || null}
        talentName={selectedFeedbackForAction?.talent?.name || null}
      />
    </Wrapper>
  );
};

export default AdminTalentPerformance;
